import { useState, useEffect, useCallback, useRef } from "react";
import useSafeAsync from "../../../../../../common/ui/useSafeAsync";
import useReadOffline from "../../../../framework/connectors/read-offline/useReadOffline";

const EXPIRES = 600000; // 10 minutes
let timeout;

interface Props {
  isDynamicUrl?: boolean;
}

const AUTO_REFRESH_LIMIT = 3;

const useAsyncAppUrl = ({ isDynamicUrl }: Props = { isDynamicUrl: true }) => {
  const [appUrl, setAppUrl] = useState();
  const { getDynamicUrl, getAppUrl } = useReadOffline();
  const safeAsync = useSafeAsync();
  const [autoRefreshDisabled, setAutoRefreshDisabled] = useState(false);
  const [isCodeExpired, setIsCodeExpired] = useState(false);
  const requestCount = useRef(0);

  const fetchUrl = useCallback(async () => {
    const fetchUrl = isDynamicUrl ? getDynamicUrl : getAppUrl;
    const response = await safeAsync(fetchUrl());
    if (response.ok) {
      setIsCodeExpired(false);
      if (isDynamicUrl) {
        setAppUrl(response.data?.shortLink);
      } else {
        setAppUrl(response.data?.redirect.autoSignin);
      }
    }
  }, [getDynamicUrl, isDynamicUrl, getAppUrl, setAppUrl, safeAsync]);

  useEffect(() => {
    fetchUrl();
  }, [fetchUrl]);

  useEffect(() => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      setIsCodeExpired(true);
      if (requestCount.current < AUTO_REFRESH_LIMIT) {
        requestCount.current++;
        fetchUrl();
      } else {
        setAutoRefreshDisabled(true);
      }
    }, EXPIRES);

    return () => {
      clearTimeout(timeout);
    };
  }, [appUrl, requestCount, fetchUrl]);

  return { appUrl, fetchUrl, autoRefreshDisabled, isCodeExpired };
};

export default useAsyncAppUrl;
